/**
 *  @class
 *  @function FreeShipping
 */

// Set to false to silence the diagnostic log at the bottom of update().
const FREE_SHIPPING_DEBUG = true;

// Buyer country, shared across every <free-shipping> element on the page.
// null until resolved, so update() falls back to the display currency.
let detectedCountry = null;
let countrySource = null;
let countryLookup = null;

const COUNTRY_CACHE_KEY = 'free-shipping-country-v2';

// Every market on this shop prices in USD (Markets > International > Currency:
// US Dollar), and Shopify keeps a buyer in the shop's default market until they
// pick a country themselves. So neither the display currency nor the default
// localization country says anything about where the buyer actually is - the
// only reliable signal is an IP lookup, tried in this order.
const COUNTRY_PROVIDERS = [
  // Shopify's own geolocation endpoint: same-origin, unmetered, and invisible to
  // the tracker blocklists that swallow third-party IP lookups.
  {
    name: 'shopify-geolocation',
    run: function () {
      let root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';

      return fetchJson(root + 'browsing_context_suggestions.json?source=geolocation_recommendation').then((data) => {
        let country = data && data.detected_values && data.detected_values.country;
        return (country && country.handle) || null;
      });
    }
  },
  // Keyless, CORS-enabled, and not rate limited the way ipapi.co's free tier is.
  {
    name: 'api.country.is',
    run: function () {
      return fetchJson('https://api.country.is/').then((data) => (data && data.country) || null);
    }
  },
  {
    name: 'ipapi.co',
    run: function () {
      return fetchJson('https://ipapi.co/json/').then((data) => (data && data.country_code) || null);
    }
  }
];

function readCachedCountry() {
  try {
    return sessionStorage.getItem(COUNTRY_CACHE_KEY);
  } catch (error) {
    return null;
  }
}

function cacheCountry(code) {
  try {
    sessionStorage.setItem(COUNTRY_CACHE_KEY, code);
  } catch (error) {
    // Storage blocked (private mode, cookie settings) - just look up again next page.
  }
}

function fetchJson(url) {
  let controller = new AbortController();
  let timeoutId = setTimeout(() => controller.abort(), 3000);

  return fetch(url, { signal: controller.signal })
    .then((response) => (response.ok ? response.json() : null))
    .catch(() => null)
    .finally(() => clearTimeout(timeoutId));
}

// The Boost app ships its own global formatMoney() that takes dollars, not cents
// (assets/boost-sd-custom-recommendation.js), and it replaces the theme's on every
// page that loads it. Formatting here keeps these amounts right whichever one wins.
function formatCents(cents, format) {
  let formatString = format || '${{amount}}';
  let match = formatString.match(/\{\{\s*(\w+)\s*\}\}/);
  let placeholder = match ? match[1] : 'amount';

  let precision = placeholder.indexOf('no_decimals') !== -1 ? 0 : 2;
  let thousands = ',';
  let decimal = '.';
  if (placeholder.indexOf('comma_separator') !== -1) {
    thousands = '.';
    decimal = ',';
  } else if (placeholder.indexOf('apostrophe_separator') !== -1) {
    thousands = "'";
  } else if (placeholder.indexOf('space_separator') !== -1) {
    thousands = ' ';
    decimal = ',';
  }

  let amount = Number(cents);
  if (isNaN(amount)) amount = 0;

  let parts = (amount / 100).toFixed(precision).split('.');
  let whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
  let fraction = parts[1] ? decimal + parts[1] : '';

  return formatString.replace(/\{\{\s*\w+\s*\}\}/, whole + fraction);
}

function countryName(code) {
  try {
    return new Intl.DisplayNames(['en'], { type: 'region' }).of(code) || code;
  } catch (error) {
    return code;
  }
}

function resolveCountry(code, source) {
  if (!code) return null;

  detectedCountry = String(code).toUpperCase();
  countrySource = source;
  return detectedCountry;
}

// Walk the providers in order, stopping at the first one that returns a country.
function runProviders(index) {
  if (index >= COUNTRY_PROVIDERS.length) return Promise.resolve(null);

  let provider = COUNTRY_PROVIDERS[index];
  return provider.run().then((code) => {
    if (code) return resolveCountry(code, provider.name);

    console.log('[free-shipping] ' + provider.name + ' returned no country, trying next provider');
    return runProviders(index + 1);
  });
}

// Testing hook: append ?fs_country=GB to any URL (or set window.freeShippingCountry
// before this script runs) to force a country without a VPN.
function forcedCountry() {
  try {
    let param = new URLSearchParams(window.location.search).get('fs_country');
    if (param) return param;
  } catch (error) {
    // Old browser without URLSearchParams - fall through to the global.
  }

  return window.freeShippingCountry || null;
}

function detectCountry() {
  if (countryLookup) return countryLookup;

  let localization = window.freeShippingLocalization || {};
  let resolvedSynchronously = null;

  let forced = forcedCountry();
  if (forced) {
    resolvedSynchronously = resolveCountry(forced, 'manual-override');
  } else if (localization.country && localization.shop_country && localization.country !== localization.shop_country) {
    // The buyer moved themselves out of the shop's default market, so this is a
    // deliberate choice and beats geolocation. A country matching the shop's own
    // is just the default and tells us nothing.
    resolvedSynchronously = resolveCountry(localization.country, 'shopify-markets');
  } else {
    resolvedSynchronously = resolveCountry(readCachedCountry(), 'session-cache');
  }

  if (resolvedSynchronously) {
    countryLookup = Promise.resolve(resolvedSynchronously);
    return countryLookup;
  }

  countryLookup = runProviders(0).then((code) => {
    if (code) {
      cacheCountry(code);
    } else if (localization.country) {
      // Every lookup failed. Shopify's default market country is a poor guess,
      // but it beats leaving the bar on whatever the server happened to render.
      resolveCountry(localization.country, 'shopify-default (all lookups failed)');
    } else {
      console.log('[free-shipping] every country lookup failed, falling back to display currency');
    }

    document.querySelectorAll('free-shipping').forEach((el) => el.update());
    return detectedCountry;
  });

  return countryLookup;
}

if (!customElements.get('free-shipping')) {
  class FreeShipping extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      detectCountry();
      if (this.querySelector('.free-shipping--message')) {
        this.update();
      } else if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this.update(), { once: true });
      } else {
        requestAnimationFrame(() => this.update());
      }
    }

    update() {
      let total = parseInt(this.dataset.cartTotal, 10);
      let step1 = parseInt(this.dataset.step1, 10);
      let step2Us = parseInt(this.dataset.step2, 10);
      let step2Intl = parseInt(this.dataset.step2Intl, 10) || step2Us;
      let isUS, source;

      if (detectedCountry) {
        isUS = detectedCountry === 'US';
        source = countrySource;
      } else {
        // Only reached while the lookup is still in flight, or if all of them fail.
        let currency = (window.Shopify && Shopify.currency && Shopify.currency.active) || 'USD';
        isUS = currency === 'USD';
        source = 'display-currency (' + currency + ')';
      }
      let step2 = isUS ? step2Us : step2Intl;

      let state, remaining;
      if (total < step1) {
        state = 'before-checkout';
        remaining = step1 - total;
      } else if (total < step2) {
        state = 'before-shipping';
        remaining = step2 - total;
      } else {
        state = 'complete';
        remaining = 0;
      }

      let percentage = step2 > 0 ? Math.min(total / step2, 1) : 1;
      this.style.setProperty('--percentage', percentage);

      let format = (window.theme && window.theme.settings && window.theme.settings.money_with_currency_format) || "${{amount}}";

      // Show the message matching the current state and update its live amount.
      let messages = this.querySelectorAll('.free-shipping--message');
      messages.forEach((message) => {
        let active = message.dataset.state === state;
        message.classList.toggle('is-hidden', !active);

        if (active && remaining > 0) {
          let amountText = message.querySelector('span');
          if (amountText) {
            amountText.innerHTML = formatCents(remaining, format);
          }
        }
      });

      let stepOne = this.querySelector('.free-shipping--step-1');
      let stepTwo = this.querySelector('.free-shipping--step-2');
      if (stepOne) {
        stepOne.style.left = (step2 > 0 ? Math.min((step1 / step2) * 100, 100) : 0) + '%';
        stepOne.classList.toggle('is-complete', total >= step1);
      }
      if (stepTwo) {
        stepTwo.classList.toggle('is-complete', total >= step2);
        // The server renders the US amount in the tooltip; keep it on the same
        // threshold as the rest of the bar.
        stepTwo.title = formatCents(step2, format);
      }

      let stepTwoText = stepTwo && stepTwo.querySelector('.step-icon-text');
      if (stepTwoText) {
        let text = isUS ? stepTwoText.dataset.textUs : stepTwoText.dataset.textIntl;
        if (text) stepTwoText.textContent = text.trim();
      }

      if (FREE_SHIPPING_DEBUG) {
        let dollars = (cents) => '$' + (cents / 100).toFixed(2);
        console.log('[free-shipping]', {
          country: detectedCountry || 'unresolved',
          countryName: detectedCountry ? countryName(detectedCountry) : 'unresolved',
          source: source,
          region: isUS ? 'US' : 'International',
          freeShippingAt: dollars(step2),
          usThreshold: dollars(step2Us),
          intlThreshold: dollars(step2Intl),
          cartTotal: dollars(total),
          state: state,
          remaining: dollars(remaining),
          shopifySaysCountry: (window.freeShippingLocalization || {}).country || 'n/a',
          shopifySaysCurrency: (window.Shopify && Shopify.currency && Shopify.currency.active) || 'n/a'
        });
      }
    }
  }

  customElements.define('free-shipping', FreeShipping);
}
